"use strict";
var _a, _b, _c;
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomersService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const customer_entity_1 = require("./customer.entity");
const address_entity_1 = require("./address.entity");
const interaction_log_entity_1 = require("./interaction-log.entity");
let CustomersService = class CustomersService {
    constructor(customerRepo, addressRepo, logRepo) {
        this.customerRepo = customerRepo;
        this.addressRepo = addressRepo;
        this.logRepo = logRepo;
    }
    /** Search customers by phone, name, email, or patientId */
    async search(query) {
        return this.customerRepo.find({
            where: [
                { phone: (0, typeorm_2.ILike)(`%${query}%`) },
                { name: (0, typeorm_2.ILike)(`%${query}%`) },
                { email: (0, typeorm_2.ILike)(`%${query}%`) },
                { patientId: (0, typeorm_2.ILike)(`%${query}%`) },
            ],
            relations: ['addresses'],
            take: 20,
        });
    }
    /** Find a customer by exact phone number. Returns null if not found. */
    async findByPhone(phone) {
        // Strip non-digits and try the last 10 digits to handle +91 prefix variations
        const digits = phone.replace(/\D/g, '');
        const last10 = digits.slice(-10);
        return this.customerRepo.findOne({
            where: [{ phone: digits }, { phone: last10 }],
        });
    }
    async findById(id) {
        const customer = await this.customerRepo.findOne({
            where: { id },
            relations: ['addresses', 'orders', 'medicalDocs', 'appointments', 'appointments.doctor', 'tickets'],
        });
        if (!customer)
            throw new common_1.NotFoundException(`Customer #${id} not found`);
        return customer;
    }
    async create(dto) {
        const payload = this.normalizeCustomerPayload(dto);
        // Check phone uniqueness
        const existing = await this.customerRepo.findOne({ where: { phone: payload.phone } });
        if (existing)
            throw new common_1.ConflictException('Phone number already registered');
        const patientId = await this.generatePatientId();
        const customer = this.customerRepo.create({ ...payload, patientId });
        return this.customerRepo.save(customer);
    }
    async update(id, dto) {
        await this.customerRepo.update(id, this.normalizeCustomerPayload(dto));
        return this.findById(id);
    }
    async addAddress(customerId, dto) {
        const customer = await this.findById(customerId);
        if (dto.isDefault) {
            await this.addressRepo.update({ customer: { id: customerId } }, { isDefault: false });
        }
        const address = this.addressRepo.create({ ...dto, customer });
        return this.addressRepo.save(address);
    }
    async updateAddress(customerId, addressId, dto) {
        const address = await this.addressRepo.findOne({ where: { id: addressId, customer: { id: customerId } } });
        if (!address)
            throw new common_1.NotFoundException(`Address #${addressId} not found`);
        if (dto.isDefault) {
            await this.addressRepo.update({ customer: { id: customerId } }, { isDefault: false });
        }
        await this.addressRepo.update(addressId, dto);
        return this.addressRepo.findOne({ where: { id: addressId } });
    }
    async getInteractionLog(customerId) {
        return this.logRepo.find({
            where: { customer: { id: customerId } },
            order: { createdAt: 'DESC' },
            take: 50,
        });
    }
    async addLog(customerId, type, description, agentId, relatedEntityId, relatedEntityType) {
        const log = this.logRepo.create({
            customer: { id: customerId },
            agent: agentId ? { id: agentId } : undefined,
            type, description, relatedEntityId, relatedEntityType,
        });
        return this.logRepo.save(log);
    }
    // Promote new → repeat on second order
    async promoteToRepeat(customerId) {
        await this.customerRepo.update(customerId, { patientType: customer_entity_1.PatientType.REPEAT });
    }
    async generatePatientId() {
        const year = new Date().getFullYear();
        const count = await this.customerRepo.count();
        const seq = String(count + 1).padStart(5, '0');
        return `PID-${year}-${seq}`;
    }
    normalizeCustomerPayload(dto) {
        const normalizeText = (value) => {
            if (typeof value !== 'string')
                return value ?? null;
            const trimmed = value.trim();
            return trimmed.length ? trimmed : null;
        };
        const normalizeChannel = (value) => {
            const normalized = normalizeText(value);
            if (normalized === 'opd')
                return 'opd_camp';
            return normalized;
        };
        const normalizeBloodGroup = (value) => {
            const normalized = normalizeText(value);
            return normalized?.replace('−', '-') ?? null;
        };
        return {
            ...(dto.name !== undefined ? { name: typeof dto.name === 'string' ? dto.name.trim() : dto.name } : {}),
            ...(dto.phone !== undefined ? { phone: typeof dto.phone === 'string' ? dto.phone.trim() : dto.phone } : {}),
            ...(dto.altPhone !== undefined ? { altPhone: normalizeText(dto.altPhone) } : {}),
            ...(dto.email !== undefined ? { email: normalizeText(dto.email) } : {}),
            ...(dto.dob !== undefined ? { dob: normalizeText(dto.dob) } : {}),
            ...(dto.gender !== undefined ? { gender: normalizeText(dto.gender) } : {}),
            ...(dto.patientType !== undefined ? { patientType: normalizeText(dto.patientType) } : {}),
            ...(dto.channel !== undefined ? { channel: normalizeChannel(dto.channel) } : {}),
            ...(dto.knownConditions !== undefined ? { knownConditions: normalizeText(dto.knownConditions) } : {}),
            ...(dto.otherConditions !== undefined ? { otherConditions: normalizeText(dto.otherConditions) } : {}),
            ...(dto.knownAllergies !== undefined ? { knownAllergies: normalizeText(dto.knownAllergies) } : {}),
            ...(dto.currentMedicines !== undefined ? { currentMedicines: normalizeText(dto.currentMedicines) } : {}),
            ...(dto.bloodGroup !== undefined ? { bloodGroup: normalizeBloodGroup(dto.bloodGroup) } : {}),
        };
    }
};
exports.CustomersService = CustomersService;
exports.CustomersService = CustomersService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(customer_entity_1.Customer)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(address_entity_1.Address)),
    tslib_1.__param(2, (0, typeorm_1.InjectRepository)(interaction_log_entity_1.InteractionLog)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object, typeof (_c = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _c : Object])
], CustomersService);
